import type { JSX } from 'react';

import { formatKpiUnit } from '@/components/kpis/KpiUnitDisplay';
import { StagedFilesPicker } from '@/components/logsheets-maker/StagedFilesPicker';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatMachineName } from '@/lib/utils/machine-label';
import type {
  BulkRowError,
  MakerKpiForMachine,
} from '@/types/logsheets-maker';

/**
 * Phase 24 (v1.4) — BulkMachineCard
 *
 * Renders one machine row with a Skip / Worked / Idle 3-segment toggle.
 *
 *  - Skip → only the header (machine name + segmented control)
 *  - Worked → expands inline with KPI inputs (prefilled from TargetPerShift
 *             when row.kpiOverrides[key] is undefined), WorkDescription
 *             textarea, Notes textarea.
 *  - Idle → expands inline with optional IdleReason + Notes textarea.
 *
 * If `duplicateBadge` is non-null, shows a yellow inline alert.
 * If `rowError` is non-null, shows a red inline alert.
 *
 * Mobile-first: tap targets ≥ 44 px, segmented control as 3 side-by-side
 * buttons.
 */

export type BulkRowState = 'skip' | 'work' | 'idle';

export interface BulkRowDraft {
  readonly workDescription: string;
  readonly idleReason: string;
  readonly notes: string;
  readonly kpiOverrides: Record<string, number | string | null>;
  /**
   * Files staged in browser memory while the maker fills the bulk wizard.
   * Uploaded by BulkMachineStep after the bulk-create POST returns this
   * row's createdId. Cleared when the row is toggled to Skip.
   */
  readonly stagedFiles: readonly File[];
}

export interface BulkMachineCardProps {
  readonly machine: {
    readonly machineId: number;
    readonly machineCode: string;
    readonly machineName: string;
    readonly vehicleNumber: string | null;
  };
  // REVIEW L-4: `workOrderId` prop was declared but never used (vestigial draft).
  // Parent now passes nothing — KPI fetch happens at the step level, not per card.
  readonly state: BulkRowState;
  readonly row: BulkRowDraft;
  /** Undefined = loading; empty array = no KPIs. */
  readonly kpis: readonly MakerKpiForMachine[] | undefined;
  /** Non-null = an existing entry exists for this machine on this date. */
  readonly duplicateBadge: string | null;
  readonly rowError?: BulkRowError | null;
  readonly onStateChange: (next: BulkRowState) => void;
  readonly onRowChange: (patch: Partial<BulkRowDraft>) => void;
}

/** Resolve the value to render in the KPI input (override wins; falls back
 * to the KPI's `targetPerShift`). Returns '' for null/undefined targets. */
function resolveKpiInputValue(
  kpi: MakerKpiForMachine,
  override: number | string | null | undefined,
): string {
  if (override !== undefined) {
    if (override === null) return '';
    if (typeof override === 'number' && Number.isFinite(override)) return String(override);
    if (typeof override === 'string') return override;
    return '';
  }
  if (kpi.targetPerShift !== null && Number.isFinite(kpi.targetPerShift)) {
    return String(kpi.targetPerShift);
  }
  return '';
}

export function BulkMachineCard({
  machine,
  state,
  row,
  kpis,
  duplicateBadge,
  rowError,
  onStateChange,
  onRowChange,
}: BulkMachineCardProps): JSX.Element {
  const { machineId, machineCode, machineName, vehicleNumber } = machine;

  function segButton(target: BulkRowState, label: string): JSX.Element {
    const active = state === target;
    return (
      <Button
        type="button"
        variant={active ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          onStateChange(target);
        }}
        data-testid={`bulk-row-${String(machineId)}-toggle-${target}`}
        className="flex-1 min-h-11"
      >
        {label}
      </Button>
    );
  }

  return (
    <Card data-testid={`bulk-row-${String(machineId)}`}>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">
              {formatMachineName(machineName, vehicleNumber)}
            </div>
            <div className="text-xs text-muted-foreground font-mono truncate">{machineCode}</div>
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          {segButton('work', 'Worked')}
          {segButton('idle', 'Idle')}
          {segButton('skip', 'Skip')}
        </div>

        {duplicateBadge !== null && (
          <div
            className="rounded-md border border-amber-400/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid={`bulk-row-${String(machineId)}-dup-badge`}
            role="status"
          >
            You already have a {duplicateBadge} entry for this machine on this date.
          </div>
        )}

        {rowError !== null && rowError !== undefined && (
          <Alert
            variant="destructive"
            data-testid={`bulk-row-${String(machineId)}-error`}
          >
            <AlertTitle>Row error</AlertTitle>
            <AlertDescription>{rowError.message}</AlertDescription>
          </Alert>
        )}

        {state === 'work' && (
          <div className="space-y-3 pt-1">
            {kpis === undefined && (
              <p className="text-xs text-muted-foreground">Loading KPIs…</p>
            )}
            {kpis?.length === 0 && (
              <p className="text-xs text-muted-foreground">No KPIs defined for this machine.</p>
            )}
            {kpis !== undefined && kpis.length > 0 && (
              <div className="space-y-2">
                {kpis.map((kpi) => {
                  const overrideKey = `kpi:${String(kpi.id)}`;
                  const inputValue = resolveKpiInputValue(kpi, row.kpiOverrides[overrideKey]);
                  return (
                    <div key={kpi.id} className="space-y-1">
                      <Label htmlFor={`bulk-row-${String(machineId)}-kpi-${String(kpi.id)}`}>
                        {kpi.name}{' '}
                        <span className="text-xs text-muted-foreground">
                          ({formatKpiUnit(kpi.unit, kpi.customUnitText)})
                        </span>
                      </Label>
                      <Input
                        id={`bulk-row-${String(machineId)}-kpi-${String(kpi.id)}`}
                        type="text"
                        inputMode="decimal"
                        value={inputValue}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const next: Record<string, number | string | null> = {
                            ...row.kpiOverrides,
                          };
                          if (raw === '') {
                            next[overrideKey] = null;
                          } else {
                            // REVIEW FF-022: keep the raw string while editing
                            // so trailing-dot or partial-decimal inputs ("1.",
                            // "1.5") aren't lost when Number(raw) round-trips
                            // and re-renders. Coerce at submit time only —
                            // `buildKpiValuesForRow` in BulkMachineStep already
                            // handles string→number normalisation when posting.
                            next[overrideKey] = raw;
                          }
                          onRowChange({ kpiOverrides: next });
                        }}
                        data-testid={`bulk-row-${String(machineId)}-kpi-${String(kpi.id)}`}
                        className="min-h-11"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor={`bulk-row-${String(machineId)}-workdescription`}>
                Work description
              </Label>
              <Textarea
                id={`bulk-row-${String(machineId)}-workdescription`}
                rows={2}
                value={row.workDescription}
                onChange={(e) => {
                  onRowChange({ workDescription: e.target.value });
                }}
                data-testid={`bulk-row-${String(machineId)}-workdescription`}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={`bulk-row-${String(machineId)}-notes`}>Notes</Label>
              <Textarea
                id={`bulk-row-${String(machineId)}-notes`}
                rows={2}
                value={row.notes}
                onChange={(e) => {
                  onRowChange({ notes: e.target.value });
                }}
                data-testid={`bulk-row-${String(machineId)}-notes`}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Documents</Label>
              <StagedFilesPicker
                files={row.stagedFiles}
                onChange={(next) => {
                  onRowChange({ stagedFiles: next });
                }}
                idPrefix={`bulk-row-${String(machineId)}-staged`}
                testIdPrefix={`bulk-row-${String(machineId)}-staged-files`}
              />
            </div>
          </div>
        )}

        {state === 'idle' && (
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label htmlFor={`bulk-row-${String(machineId)}-idlereason`}>Idle reason</Label>
              <Input
                id={`bulk-row-${String(machineId)}-idlereason`}
                type="text"
                inputMode="text"
                value={row.idleReason}
                onChange={(e) => {
                  onRowChange({ idleReason: e.target.value });
                }}
                placeholder="e.g. Maintenance, Breakdown"
                data-testid={`bulk-row-${String(machineId)}-idlereason`}
                className="min-h-11"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={`bulk-row-${String(machineId)}-notes`}>Notes</Label>
              <Textarea
                id={`bulk-row-${String(machineId)}-notes`}
                rows={2}
                value={row.notes}
                onChange={(e) => {
                  onRowChange({ notes: e.target.value });
                }}
                data-testid={`bulk-row-${String(machineId)}-notes`}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Documents</Label>
              <StagedFilesPicker
                files={row.stagedFiles}
                onChange={(next) => {
                  onRowChange({ stagedFiles: next });
                }}
                idPrefix={`bulk-row-${String(machineId)}-staged`}
                testIdPrefix={`bulk-row-${String(machineId)}-staged-files`}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
